import { useRuntimeConfig } from '#imports'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { priceToMinorUnits } from '@backmarket/utils/math/priceToMinorUnits'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'

import type { GetPaymentMethodsOptions } from './getPaymentMethods.specs'
import { mapGetPaymentMethodsResponseToLegacyResponse } from './getPaymentMethods.utils'

export function usePaymentMethods() {
  const $httpFetch = useHttpFetch()
  const { payment } = useRuntimeConfig().public
  const paymentMethodsApiMigrationEnabledCountries =
    payment?.FF_PAYMENT_METHODS_API_MIGRATION_ENABLED_COUNTRIES ?? ''

  async function legacyGetPaymentMethods({
    countryCode,
    billingCountry,
    listings,
    bagPrice,
    insuranceSubscription,
    features,
    businessScope,
  }: GetPaymentMethodsOptions) {
    const mergedFeatures = [
      ...(features ?? []),
      ...(businessScope ? [businessScope] : []),
    ]

    const data = await $httpFetch('/payment/payment_methods', {
      query: {
        country_code: countryCode,
        billing_country: billingCountry,
        listings: listings?.join(','),
        bag_price: bagPrice?.amount,
        insurance_subscription: insuranceSubscription,
        features: mergedFeatures.join(',') || undefined,
      },
    })

    return camelizeKeys(data)
  }

  async function getPaymentMethods({
    tenant,
    countryCode,
    billingCountry,
    listings,
    bagPrice,
    features,
    businessScope,
  }: GetPaymentMethodsOptions) {
    const data = await $httpFetch('/payment/payment-methods', {
      query: {
        tenant,
        country: countryCode,
        billing_country: billingCountry,
        client_platform: 'WEB',
        listing_ids: listings?.length ? listings?.join(',') : undefined,
        price: bagPrice ? priceToMinorUnits(bagPrice) : undefined,
        features: features?.length ? features?.join(',') : undefined,
        business_scope: businessScope,
      },
    })

    return mapGetPaymentMethodsResponseToLegacyResponse(data, { countryCode })
  }

  return async (options: GetPaymentMethodsOptions) => {
    if (options.tenant === 'TRADEIN') {
      return getPaymentMethods(options)
    }

    if (
      paymentMethodsApiMigrationEnabledCountries.includes(options.countryCode)
    ) {
      return getPaymentMethods(options)
    }

    return legacyGetPaymentMethods(options)
  }
}
